import SearchIcon from '@mui/icons-material/Search';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import {
  Avatar,
  IconButton,
  InputAdornment,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  PopoverProps,
  TextField,
  Tooltip,
} from '@mui/material';
import { menuOpeningProps } from 'components/brand-voice/menu-opening-position-props';
import { InfiniteDropdownIntersectionObserver } from 'components/InfiniteDropdownIntersectionObserver';
import {
  EMPTY_OPTION,
  TargetAudienceOption,
} from 'features/target-audience/components/AutocompleteDropdownMenuContent';
import { CreateTargetAudienceMenuItemContent } from 'features/target-audience/components/CreateTargetAudienceMenuItemContent';
import { EmptyTargetAudienceMenuItemContent } from 'features/target-audience/components/EmptyTargetAudienceMenuItemContent';
import { TargetAudienceAvatar } from 'features/target-audience/components/TargetAudienceAvatar';
import { TargetAudienceMenuItemContent } from 'features/target-audience/components/TargetAudienceMenuItemContent';
import { useRedirectToTargetAudienceCreation } from 'features/target-audience/useRedirectToTargetAudienceCreation';
import { useTargetAudienceAutocompleteData } from 'features/target-audience/useTargetAudienceAutocompleteData';
import {
  ChangeEvent,
  MouseEvent as ReactMouseEvent,
  ReactElement,
  useEffect,
  useState,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { GAEvents } from 'services/tracking/GAEvents';
import styled from 'styled-components';
import useTr from 'utils/hooks/useTr';
type Props = {
  targetLanguage?: string;
  targetCountry?: string;
  menuOpeningDirection?: 'top' | 'bottom' | 'right';
  selectedTargetAudienceId?: string;
  dropDownProps?: Partial<PopoverProps>;
  customTrigger?: (props: {
    onClick: (event: ReactMouseEvent) => void;
    selectedOption: TargetAudienceOption | null;
    open: boolean;
  }) => ReactElement;

  onTargetAudienceIdChange?: (
    targetAudienceId: string | null
  ) => void | boolean | PromiseLike<void | boolean>;
};

export const ChatTargetAudienceSelect = ({
  targetLanguage,
  targetCountry,
  menuOpeningDirection = 'bottom',
  selectedTargetAudienceId,
  customTrigger,
  onTargetAudienceIdChange,
}: Props) => {
  const translate = useTr();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [inputValue, setInputValue] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [selectedOption, setSelectedOption] = useState<TargetAudienceOption | null>(null);

  const { isLoading, targetAudiences, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useTargetAudienceAutocompleteData({
      initialTargetAudienceId: selectedTargetAudienceId,
      targetLanguage,
      targetCountry,
      search: isSearching ? inputValue : undefined,
    });

  useEffect(() => {
    // Update local state when selectedTargetAudienceId changes from parent component
    if (targetAudiences.length > 0) {
      if (selectedTargetAudienceId) {
        const foundTargetAudience = targetAudiences.find(
          audience => audience.id === selectedTargetAudienceId
        );
        if (foundTargetAudience && foundTargetAudience.id !== selectedOption?.id) {
          setSelectedOption(foundTargetAudience);
        }
      } else {
        // If selectedTargetAudienceId is null/undefined, clear the selection
        if (selectedOption) {
          setSelectedOption(null);
        }
      }
    } else {
      setSelectedOption(null);
    }
  }, [
    targetAudiences,
    selectedTargetAudienceId,
    selectedOption?.id,
    selectedOption,
    targetLanguage,
  ]);

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    setInputValue(value);
    setIsSearching(true);
  };

  const handleOpen = (event: ReactMouseEvent) => {
    setAnchorEl(event.currentTarget as HTMLElement);
  };

  const handleOptionSelect = async (option: TargetAudienceOption) => {
    GAEvents.targetAudienceSelectedChat({
      targetAudienceId: option.id,
    });

    const updateLocalState = () => {
      setSelectedOption(option);
      setInputValue('');
      setAnchorEl(null);
      setIsSearching(false);
    };

    try {
      if (option.id === EMPTY_OPTION.id) {
        updateLocalState();

        await onTargetAudienceIdChange?.(null);
        return;
      }

      if (onTargetAudienceIdChange) {
        const result = await onTargetAudienceIdChange(option.id || '');
        if (result === false) {
          return;
        }
      }

      updateLocalState();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error selecting target audience:', error);
    }
  };

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    setAnchorEl(null);
    setIsSearching(false);
  };

  const redirectToTargetAudienceCreation = useRedirectToTargetAudienceCreation();

  const handleCreateClick = () => {
    redirectToTargetAudienceCreation();
  };

  return (
    <>
      <Tooltip title={translate('chat.message_input.target_audience.tooltip')}>
        {customTrigger ? (
          customTrigger({
            onClick: handleOpen,
            selectedOption,
            open,
          })
        ) : (
          <StyledIconButton size="small" onClick={handleOpen} $hasSelectedOption={!!selectedOption}>
            {selectedOption ? (
              <TargetAudienceAvatar
                thumbnailUrl={selectedOption._embedded?.image?.url}
                name={selectedOption.name}
              />
            ) : (
              <StyledSupervisedUserCircleIcon />
            )}
          </StyledIconButton>
        )}
      </Tooltip>

      <StyledPopover
        anchorEl={anchorEl}
        open={open}
        onClose={handleClickAway}
        {...menuOpeningProps[menuOpeningDirection]}
      >
        <StyledTextField
          variant="standard"
          fullWidth
          inputRef={node => {
            if (node) {
              node.focus();
            }
          }}
          value={inputValue}
          onChange={handleInputChange}
          placeholder={translate('chat.message_input.target_audience.text_field.placeholder')}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        <StyledMenuList>
          {inputValue.length === 0 && (
            <div>
              <StyledMenuItem onClick={handleCreateClick}>
                <CreateTargetAudienceMenuItemContent />
              </StyledMenuItem>

              <StyledMenuItem
                key={EMPTY_OPTION.id}
                onClick={() => handleOptionSelect(EMPTY_OPTION)}
                selected={!selectedOption || selectedOption.id === EMPTY_OPTION.id}
              >
                <EmptyTargetAudienceMenuItemContent />
              </StyledMenuItem>
            </div>
          )}

          {targetAudiences.length === 0 && (
            <StyledMenuItem disabled>
              <ListItemText>
                <FormattedMessage id="target_audience.selector.no_target_audience" />
              </ListItemText>
            </StyledMenuItem>
          )}

          {targetAudiences.map(option => (
            <StyledMenuItem
              key={option.id || option.name}
              value={option.id}
              onClick={() => handleOptionSelect(option)}
              selected={selectedOption?.id === option.id}
            >
              <TargetAudienceMenuItemContent
                option={option}
                searchText={inputValue}
                isPersonal={!option.shared}
              />
            </StyledMenuItem>
          ))}
        </StyledMenuList>

        <InfiniteDropdownIntersectionObserver
          onIsInView={() => fetchNextPage()}
          isAlreadyFetching={isFetchingNextPage}
          enabled={hasNextPage}
        />
      </StyledPopover>
    </>
  );
};

const StyledIconButton = styled(IconButton)<{ $hasSelectedOption?: boolean }>`
  border: 1px solid ${({ theme }) => theme.colors.divider};

  padding: ${({ $hasSelectedOption }) => ($hasSelectedOption ? 'normal' : '3px')};
`;

const StyledSupervisedUserCircleIcon = styled(SupervisedUserCircleIcon)`
  color: ${({ theme }) => theme.colors.componentsIconDefault};
  font-size: 28px;
`;

const StyledPopover = styled(Popover)`
  .MuiPaper-root {
    width: 390px;
    max-height: 280px;
    display: flex;
    flex-direction: column;
    padding: ${({ theme }) => `${theme.spacings.two} 0`};
  }
`;

const StyledTextField = styled(TextField)`
  .MuiInputBase-root {
    border: none;
    outline: none;
    box-shadow: none;
    padding: ${({ theme }) => `${theme.spacings.two} ${theme.spacings.four}`};

    &.MuiInput-root::before {
      border-bottom: 2px solid ${({ theme }) => theme.colors.divider};
    }
  }
`;

const StyledMenuList = styled(MenuList)`
  overflow-y: auto;
  flex-grow: 1;
`;

const StyledMenuItem = styled(MenuItem)`
  padding: ${({ theme }) => `${theme.spacings.two} ${theme.spacings.four}`};
`;
